/**
 * WHN Referral Message
 *
 * When the visitor lands on a URL containing one of the parameter keys
 * configured in the "URL Parameter Message" metaobject (e.g. `?bg_ref=abc123`),
 * saves that entry's message to the cart attribute `referral_message` so it
 * travels through to checkout and the order.
 *
 * Only the key is matched; the parameter must also have a non-empty value.
 * Entries are checked in metaobject order and the first match wins. When no
 * parameter matches, the cart is left untouched (an earlier message is kept).
 *
 * `/cart/update.js` merges `attributes` into the existing ones, so other cart
 * attributes are preserved.
 *
 * Config is read once from the JSON blob rendered by
 * `snippets/whn-referral-message-data.liquid`.
 */
import { fetchConfig } from '@theme/utilities';

const DATA_ELEMENT_ID = 'whn-referral-message-data';
const ATTRIBUTE_KEY = 'referral_message';

const routes = (typeof Theme !== 'undefined' ? Theme.routes : null) ?? {};
const shopifyRoot = window.Shopify?.routes?.root ?? '/';

/** @param {string} key */
const normalizeKey = (key) => key.trim().toLowerCase();

/**
 * Liquid HTML-escapes cart attribute values (`don't` → `don&#39;t`), so decode
 * before comparing with the raw metaobject message.
 * @param {string | null} value
 */
function decodeHtml(value) {
  if (!value) return value;
  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

/**
 * @returns {{ rules: Array<{parameters: string[], message: string}>, current: string | null } | null}
 */
function readConfig() {
  const element = document.getElementById(DATA_ELEMENT_ID);
  if (!element?.textContent) return null;

  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    console.error('[WHN] Referral message: invalid config JSON', error);
    return null;
  }
}

/**
 * @param {Array<{parameters: string[], message: string}>} rules
 * @returns {string | null}
 */
function findMessage(rules) {
  const presentKeys = new Set();
  for (const [key, value] of new URLSearchParams(window.location.search)) {
    if (value.trim() !== '') presentKeys.add(normalizeKey(key));
  }
  if (presentKeys.size === 0) return null;

  const rule = rules.find((rule) => rule.parameters.some((parameter) => presentKeys.has(normalizeKey(parameter))));
  return rule?.message ?? null;
}

async function init() {
  const config = readConfig();
  if (!config || !Array.isArray(config.rules) || config.rules.length === 0) return;

  const message = findMessage(config.rules);
  if (!message || message === decodeHtml(config.current)) return;

  try {
    const response = await fetch(
      routes.cart_update_url ?? `${shopifyRoot}cart/update.js`,
      fetchConfig('json', { body: JSON.stringify({ attributes: { [ATTRIBUTE_KEY]: message } }) })
    );
    if (!response.ok) {
      console.error('[WHN] Referral message: cart update failed', response.status);
    }
  } catch (error) {
    console.error('[WHN] Referral message: cart update failed', error);
  }
}

init();
